import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Badge,
  Button,
  useToast,
} from "@repo/ui";
import { formatToApiDate } from "@repo/utils";
import { DateInput } from "@/components/forms";
import { api, ApiClientError } from "@/lib/api/client";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { getPriorityColor, getOverdueDays } from "@/lib/utils/reminder-format";
import { EntityReferenceBadge } from "@/components/reminders/entity-reference-badge";
import type { Reminder } from "@/hooks/use-reminders";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  CalendarDays,
  Clock,
  Loader2,
  X,
  Building2,
  User,
  ExternalLink,
  UserCircle,
} from "lucide-react";

interface DueRemindersModalProps {
  reminders: Reminder[];
  onReminderChanged?: () => void;
}

export function DueRemindersModal({
  reminders,
  onReminderChanged,
}: DueRemindersModalProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const navigate = useNavigate();
  const today = formatToApiDate(new Date());

  // Only show reminders that are due today or overdue
  const dueReminders = reminders.filter(
    (r) => !r.is_completed && r.due_date <= today
  );

  const [dismissed, setDismissed] = React.useState(false);
  const [handledIds, setHandledIds] = React.useState<Set<string>>(new Set());
  const [loadingId, setLoadingId] = React.useState<string | null>(null);
  const [reschedulingId, setReschedulingId] = React.useState<string | null>(
    null
  );
  const [rescheduleDate, setRescheduleDate] = React.useState("");

  const remaining = dueReminders.filter((r) => !handledIds.has(r.id));

  // Modal is open if not dismissed AND there are remaining due reminders
  const isOpen = !dismissed && remaining.length > 0;

  const overdueCount = remaining.filter((r) => r.due_date < today).length;
  const todayCount = remaining.filter((r) => r.due_date === today).length;

  const handleDismiss = () => {
    setDismissed(true);
  };

  const handleComplete = async (id: string) => {
    setLoadingId(id);
    try {
      await api.post(`/reminders/${id}/complete`, {});

      setHandledIds((prev) => new Set([...prev, id]));
      onReminderChanged?.();
      toast({ title: "Reminder marked as complete", variant: "success" });
    } catch (error) {
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async (approvalToken) => {
            await api.post(`/reminders/${id}/complete`, { approvalToken });
            setHandledIds((prev) => new Set([...prev, id]));
            onReminderChanged?.();
            toast({ title: "Reminder marked as complete", variant: "success" });
          },
          actionDescription: "Complete reminder",
        })
      )
        return;
      toast({
        title: "Error",
        description: "Failed to complete reminder. Try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingId(null);
    }
  };

  const handleReschedule = async (id: string) => {
    if (!rescheduleDate) return;

    setLoadingId(id);
    try {
      await api.patch(`/reminders/${id}`, { due_date: rescheduleDate });

      setHandledIds((prev) => new Set([...prev, id]));
      setReschedulingId(null);
      setRescheduleDate("");
      onReminderChanged?.();
      toast({ title: "Reminder rescheduled", variant: "success" });
    } catch (error) {
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async (approvalToken) => {
            await api.patch(`/reminders/${id}`, {
              due_date: rescheduleDate,
              approvalToken,
            });
            setHandledIds((prev) => new Set([...prev, id]));
            setReschedulingId(null);
            setRescheduleDate("");
            onReminderChanged?.();
            toast({ title: "Reminder rescheduled", variant: "success" });
          },
          actionDescription: "Reschedule reminder",
        })
      )
        return;
      toast({
        title: "Error",
        description: "Failed to reschedule reminder. Try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingId(null);
    }
  };

  const startReschedule = (id: string) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setRescheduleDate(formatToApiDate(tomorrow));
    setReschedulingId(id);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="sm:max-w-lg max-h-[85vh] flex flex-col [&>button:last-child]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-3">
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Reminders Need Attention
          </DialogTitle>
          <DialogDescription>
            You have{" "}
            {overdueCount > 0 && (
              <span className="font-medium text-destructive">
                {overdueCount} overdue
              </span>
            )}
            {overdueCount > 0 && todayCount > 0 && " and "}
            {todayCount > 0 && (
              <span className="font-medium text-warning">
                {todayCount} due today
              </span>
            )}
            . Please complete or reschedule each reminder.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-2 py-2">
          {remaining.map((reminder) => {
            const overdueDays = getOverdueDays(reminder.due_date);
            const isOverdue = overdueDays > 0;
            const isRescheduling = reschedulingId === reminder.id;
            const isLoading = loadingId === reminder.id;

            return (
              <div
                key={reminder.id}
                className={`rounded-xl border p-4 space-y-3 transition-colors ${
                  isOverdue
                    ? "border-destructive/30 bg-danger-soft"
                    : "border-warning-border bg-warning-soft"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block h-2 w-2 rounded-full shrink-0 ${getPriorityColor(reminder.priority)}`}
                      />
                      <h4 className="font-medium text-sm truncate">
                        {reminder.title}
                      </h4>
                    </div>
                    {reminder.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2 pl-4">
                        {reminder.description}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge
                      variant={isOverdue ? "destructive" : "outline"}
                      className={`text-xs h-5 px-1.5 gap-1 ${
                        !isOverdue ? "border-warning text-warning" : ""
                      }`}
                    >
                      <Clock className="h-3 w-3" />
                      {isOverdue ? `${overdueDays}d overdue` : "Due today"}
                    </Badge>
                    <Badge variant="outline" className="text-xs h-5 px-1.5">
                      {reminder.visibility === "pharmacy" ? (
                        <Building2 className="h-3 w-3" />
                      ) : (
                        <User className="h-3 w-3" />
                      )}
                    </Badge>
                  </div>
                </div>

                {/* Patient name and linked entity */}
                {(reminder.patient_name ||
                  (reminder.reference_type && reminder.reference_id)) && (
                  <div className="flex flex-wrap items-center gap-1.5 pl-4">
                    {reminder.patient_name && (
                      <Badge
                        variant="outline"
                        className="gap-1 text-xs h-5 px-1.5"
                      >
                        <UserCircle className="h-3 w-3 text-secondary" />
                        <span className="truncate max-w-[160px]">
                          {reminder.patient_name}
                        </span>
                      </Badge>
                    )}
                    {reminder.reference_type && reminder.reference_id && (
                      <EntityReferenceBadge
                        referenceType={reminder.reference_type}
                        referenceId={reminder.reference_id}
                      />
                    )}
                  </div>
                )}

                {isRescheduling ? (
                  <div className="flex items-center gap-2">
                    <DateInput
                      value={rescheduleDate}
                      onChange={(v) => setRescheduleDate(v)}
                      min={formatToApiDate(new Date())}
                      className="h-8 text-sm flex-1"
                    />
                    <Button
                      size="sm"
                      className="h-8 gap-1"
                      disabled={isLoading || !rescheduleDate}
                      onClick={() => handleReschedule(reminder.id)}
                    >
                      {isLoading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <CalendarDays className="h-3.5 w-3.5" />
                      )}
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8"
                      disabled={isLoading}
                      onClick={() => {
                        setReschedulingId(null);
                        setRescheduleDate("");
                      }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="h-8 gap-1.5 flex-1"
                      disabled={isLoading}
                      onClick={() => handleComplete(reminder.id)}
                    >
                      {isLoading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      )}
                      Mark Done
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1.5 flex-1"
                      disabled={isLoading}
                      onClick={() => startReschedule(reminder.id)}
                    >
                      <CalendarClock className="h-3.5 w-3.5" />
                      Reschedule
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 gap-1 px-2 text-muted-foreground hover:text-foreground shrink-0"
                      onClick={() => {
                        setDismissed(true);
                        navigate({
                          to: "/reminders",
                          search: { id: reminder.id },
                        });
                      }}
                      title="Open in Reminders"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <p className="text-xs text-muted-foreground">
            {remaining.length} reminder{remaining.length !== 1 ? "s" : ""}{" "}
            remaining
          </p>
          <Button variant="ghost" size="sm" onClick={handleDismiss}>
            Ignore for now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
